'use client';

import {
  Alert,
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import { ConnectorType } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { FormTextField } from '@/components/form/formTextField';
import type { FieldValidation } from '@/components/form/validation/fieldValidation';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';

import type { AddConnectorWizardState } from '../addConnectorWizardState';

const awsAccountIdValidation: FieldValidation = {
  validateInput: (value: string) => /^\d{12}$/.test(value),
  errorMessage: () => 'AWS Account ID must be exactly 12 digits',
};

export const AddConnectorStep = (props: StepProps<AddConnectorWizardState>) => {
  const dal = useDAL();
  const { wizardState: state, setWizardState } = props;
  const [acknowledged, setAcknowledged] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const update = (partial: Partial<AddConnectorWizardState>) =>
    setWizardState({ ...state, ...partial });

  const isAccountIdValid = /^\d{12}$/.test(state.awsAccountId);
  const isMissingFields =
    !state.awsAccountId ||
    !state.discoveryRoleName ||
    !state.dataAccessRoleName;

  const canCreate =
    !isMissingFields && isAccountIdValid && acknowledged && !creating;

  const handleCreate = async () => {
    setError(null);
    setCreating(true);
    try {
      await dal.lakehouse.createConnector(state.workspaceId, {
        name: state.connectorName,
        connectorType: ConnectorType.CONNECTOR_TYPE_AWS_ACCOUNT_CONNECTOR,
        awsAccountProperties: {
          awsAccountId: state.awsAccountId,
          discoveryRoleName: state.discoveryRoleName,
          dataAccessRoleName: state.dataAccessRoleName,
        },
      });
      void props.finish();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to create connector'
      );
    } finally {
      setCreating(false);
    }
  };

  return (
    <StepContainer
      hideStepper
      stepperIndex={0}
      canGoNext={() => canCreate}
      nextButtonText={creating ? 'Creating...' : 'Create'}
      nextButtonLoadingText='Creating...'
      backButtonText='Cancel'
      onBackClick={props.back}
      onNextClick={handleCreate}
      sx={{ overflow: 'auto', paddingX: '0px', paddingY: 'min(60px, 4vw)' }}
    >
      <Stack gap='min(60px, 4vw)' divider={<Divider />}>
        {/* Connector Details */}
        <Box paddingX='min(100px, 6vw)'>
          <Stack gap='24px' maxWidth='540px'>
            <Stack gap='8px'>
              <Typography variant='h6'>Connector Details</Typography>
              <Typography variant='body2' color='text.secondary'>
                Name the connector and specify the AWS account it connects to.
              </Typography>
            </Stack>

            <FormTextField
              title='AWS Account ID'
              size='small'
              placeholder='123456789012'
              value={state.awsAccountId}
              validation={awsAccountIdValidation}
              onChange={(e) => {
                const newAccountId = e.target.value
                  .replace(/\D/g, '')
                  .slice(0, 12);
                update({ awsAccountId: newAccountId });
              }}
            />
          </Stack>
        </Box>

        {/* IAM Roles */}
        <Box paddingX='min(100px, 6vw)'>
          <Stack gap='24px' maxWidth='540px'>
            <Stack gap='8px'>
              <Typography variant='h6'>IAM Roles</Typography>
              <Typography variant='body2' color='text.secondary'>
                Specify the IAM role names that Eon will assume in your AWS
                account for discovery and data access.
              </Typography>
            </Stack>

            <FormTextField
              title='Discovery Role Name'
              size='small'
              placeholder='e.g., EonDiscoveryRole'
              value={state.discoveryRoleName}
              onChange={(e) => update({ discoveryRoleName: e.target.value })}
            />

            <FormTextField
              title='Data Access Role Name'
              size='small'
              placeholder='e.g., EonDataAccessRole'
              value={state.dataAccessRoleName}
              onChange={(e) => update({ dataAccessRoleName: e.target.value })}
            />
          </Stack>
        </Box>

        {/* Deployment Confirmation */}
        <Box paddingX='min(100px, 6vw)'>
          <Stack gap='24px' maxWidth='540px'>
            <Stack gap='8px'>
              <Typography variant='h6'>Deployment</Typography>
              <Typography variant='body2' color='text.secondary'>
                Confirm that the required IAM roles have been deployed in your
                AWS account before creating the connector.
              </Typography>
            </Stack>

            {state.awsAccountId && (
              <Alert severity='info'>
                <Typography variant='body2'>
                  Ensure the following IAM roles are deployed in AWS account{' '}
                  <strong>{state.awsAccountId}</strong>:
                </Typography>
                <Typography
                  variant='body2'
                  component='ul'
                  sx={{ mt: '8px', pl: '16px' }}
                >
                  <li>
                    <code>
                      arn:aws:iam::{state.awsAccountId}:role/
                      {state.discoveryRoleName}
                    </code>
                  </li>
                  <li>
                    <code>
                      arn:aws:iam::{state.awsAccountId}:role/
                      {state.dataAccessRoleName}
                    </code>
                  </li>
                </Typography>
                <Typography variant='body2' sx={{ mt: '8px' }}>
                  These roles must have a trust policy allowing Eon to assume
                  them.
                </Typography>
              </Alert>
            )}

            <FormControlLabel
              control={
                <Checkbox
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                  size='small'
                />
              }
              label={
                <Typography variant='body2'>
                  I have deployed the IAM roles in my AWS account
                </Typography>
              }
            />
          </Stack>
        </Box>
      </Stack>

      {error && (
        <Box paddingX='min(100px, 6vw)' paddingTop='24px'>
          <Alert severity='error' sx={{ maxWidth: '540px' }}>
            <Typography variant='body2'>{error}</Typography>
          </Alert>
        </Box>
      )}
    </StepContainer>
  );
};
